import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { isEqual } from 'lodash'
import { WIZARD_SCREEN_TYPES } from '../constants'
import { WORKFLOW, SECTIONS, DEFAULT_DETAILS, BOARDING_FIELDS } from './config'
import WizardDialog from '../components/WizardDialog'
import BoardingWizardContext from './BoardingWizardContext'
import useWizardRouterHistory from '../hooks/useWizardRouterHistory'
import SectionNavigator from '../components/SectionNavigator'
import ExitWizardDialog from '../components/ExitWizardDialog'
import useBoardingVisit from '../../../hooks/useBoardingVisit'
import useDayRestrictions from '../../../hooks/useDayRestrictions'
import { formatArrayOfPetNames } from '../../../utils'
import DifferentFacilityBookingWarning from '../../Common/DifferentFacilityBookingWarning'
import { updatePetAsync } from '../../../actions/pets/pets.actions'

const BoardingWizard = ({ step = 0, open = false, onClose, updatedVisit = {} }) => {
	const dispatch = useDispatch()
	const [currentStep, setCurrentStep] = useState(step)
	const [canProceedFunc, setCanProceedFunc] = useState(() => () => true)
	const [showExitWizardDialog, setShowExitWizardDialog] = useState(false)
	const [goToStep, setGoToStep] = useState(null)

	const { loading, update, updateAddOns, facility } = useBoardingVisit()
	const household = useSelector((state) => state.specific.households.selected)
	const { setStepInHistory, clearHistory } = useWizardRouterHistory()
	const { dayRestrictionsMap } = useDayRestrictions()
	const user = useSelector((state) => state.general.user.user)

	// Context Related State -----------------------------------------------------
	const setWizardAttributeAndValues = (newValues) => {
		setWizardContextProvider({
			wizard_state: { ...newValues },
			setWizardAttributeAndValues
		})
	}

	const [wizardContextProvider, setWizardContextProvider] = useState({
		wizard_state: { ...DEFAULT_DETAILS },
		setWizardAttributeAndValues
	})
	// ---------------------------------------------------------------------------

	const REQUIRED_WORKFLOW = useMemo(() => {
		const { wizard_state } = wizardContextProvider
		const reqWorkflow = []

		for (const step of WORKFLOW) {
			const {
				display_step_condition,
				requires_multiple_views,
				create_multiple_views_from,
				individual_props_for_each_view_component = [],
				specific_view_in_multiple_views_not_required_when
			} = step
			if (!display_step_condition || display_step_condition(wizard_state)) {
				if (requires_multiple_views) {
					const values = wizard_state[create_multiple_views_from] // e.g wizard_state['pets']
					if (values && values.length > 0) {
						if (!requires_multiple_views(wizard_state)) {
							// if we have defined that multiple views are required, but
							// a value in wizard state signals we don't need mulitiple, such as copy all being set to true
							// we still need to show the first view
							const singleStep = { ...step }
							// copy the individual props from individual_props_for_each_view_component array into the step
							// so the view can use them.
							individual_props_for_each_view_component.forEach((attr) => {
								singleStep[attr] = values[0][attr] // e.g. singleStep['_id'] = pets[0]['_id']
							})
							singleStep['position'] = -1 // This helps us know that we don't need to show a copy to all checkbox, etc.
							reqWorkflow.push(singleStep)
						} else {
							// if multiple views are required, we will create new steps
							// based off the step in the workflow with the other props

							let position = -1

							// e.g. for pet of pets
							for (const value of values) {
								const nthStep = { ...step }

								// we may need multiple views, but sometimes one of the multiple views
								// does not need to be shown. This condition checks for that
								// and does not create the view if true
								if (specific_view_in_multiple_views_not_required_when !== undefined) {
									// e.g. ['medication', (medication) => medication.length === 0]
									const [key, testFunction] = specific_view_in_multiple_views_not_required_when
									const testValue = value[key] // e.g pet['medication]
									if (testValue && testFunction(testValue)) {
										continue
									}
								}

								// copy the individual props from individual_props_for_each_view_component array into the step
								// so the view can use them.
								// e.g. ['_id', 'name']
								individual_props_for_each_view_component.forEach((attr) => {
									nthStep[attr] = value[attr]
								})

								// Position helps us know when we have the first view in the series
								// This is used in currentComponent to pass the 'first_view_of_multiple' prop
								// This is mainly used to know when we need to show a copy to all pets checkbox.
								nthStep['position'] = position
								position++

								reqWorkflow.push(nthStep)
							}
						}
					}
				} else {
					reqWorkflow.push(step)
				}
			}
		}

		// @TODO Add this goToStep logic to all other wizards. Allows going to a specific step by type, can add attribute later
		reqWorkflow.forEach((step) => {
			step['goToStep'] = setGoToStep
		})

		return reqWorkflow
	}, [wizardContextProvider])

	const requiredSteps = useMemo(() => REQUIRED_WORKFLOW.length, [REQUIRED_WORKFLOW])

	useEffect(() => {
		const { wizard_state, setWizardAttributeAndValues } = wizardContextProvider
		if (updatedVisit) {
			const updatedVisitValues = { ...wizard_state }
			for (const key in updatedVisit) {
				if (updatedVisitValues[key] !== undefined) {
					updatedVisitValues[key] = updatedVisit[key]
				}

				if (key === BOARDING_FIELDS.dates) {
					let depositRequired = false
					for (const date of updatedVisit[key]) {
						const dayRestriction = dayRestrictionsMap.get(date)
						if (dayRestriction && dayRestriction.boarding && dayRestriction.boarding.length > 0) {
							depositRequired = dayRestriction.boarding.some(
								(restriction) => restriction.deposit_required
							)
						}
						if (depositRequired) {
							break
						}
					}
					if (depositRequired) {
						updatedVisitValues[BOARDING_FIELDS.deposit_required] = true
					}
				}

				if (key === BOARDING_FIELDS.pets) {
					const pets = updatedVisit[key]
					if (pets && pets.length > 0) {
						const petNames = pets.map(({ name }) => name)
						updatedVisitValues['petNames'] = petNames

						const petFoodSchedules = {}
						let activeMeds = []
						let defaultKennelOptions = []

						pets.forEach(({ medication, _id, food_schedule }) => {
							petFoodSchedules[_id] = Array.isArray(food_schedule) ? [...food_schedule] : []
							defaultKennelOptions.push({
								pet_id: _id,
								kennel_type: 'Standard',
								areAddonsShared: false
							})
							if (medication?.length > 0) {
								activeMeds = [...activeMeds, ...medication.filter((m) => m.active !== false)]
							}
						})

						updatedVisitValues[BOARDING_FIELDS.pet_food_schedules] = petFoodSchedules
						updatedVisitValues[BOARDING_FIELDS.medications] = activeMeds
						if (updatedVisit[BOARDING_FIELDS.boarding_options].length === 0) {
							updatedVisitValues[BOARDING_FIELDS.boarding_options] = defaultKennelOptions
						}
					}
				}
			}

			if (!updatedVisit.hasOwnProperty(BOARDING_FIELDS.sharing_kennels)) {
				updatedVisitValues[BOARDING_FIELDS.sharing_kennels] =
					updatedVisit[BOARDING_FIELDS.pets]?.length > 1
			}

			setWizardAttributeAndValues(updatedVisitValues)
		}
	}, [updatedVisit])

	useEffect(() => {
		if (goToStep) {
			const { screenType } = goToStep
			const step = REQUIRED_WORKFLOW.findIndex((step) => step.type === screenType)
			if (step > -1) {
				setCurrentStep(step)
				setStepInHistory(step)
			}
			setGoToStep(null)
		}
	}, [goToStep, REQUIRED_WORKFLOW, setCurrentStep, setStepInHistory, setGoToStep])

	useEffect(() => {
		const stepInt = parseInt(step, 10)
		setCurrentStep(stepInt)
	}, [step])

	const userFirstName = useMemo(() => {
		if (!user || !user.displayName) {
			return 'WhoAreYou?'
		}

		const [first] = user.displayName.split(' ')
		return first || 'Skipper'
	}, [user])

	const handleCloseRequest = useCallback(() => {
		setShowExitWizardDialog(true)
	}, [setShowExitWizardDialog])

	const handleCloseRequestCancel = useCallback(() => {
		setShowExitWizardDialog(false)
	}, [setShowExitWizardDialog])

	const handleStart = useCallback(() => {
		setCurrentStep(1)
		setStepInHistory(1)
	}, [setCurrentStep, setStepInHistory])

	const handleExit = useCallback(() => {
		if (onClose) {
			onClose()
		}
	}, [onClose])

	const handleOnClose = useCallback(() => {
		if (onClose && !loading) {
			setShowExitWizardDialog(false)
			clearHistory()
			onClose()
		}
	}, [onClose, loading, setShowExitWizardDialog, clearHistory, update])

	/**
	 * returns the configuration object for the current step
	 */
	const currentWorflowStepConfig = useMemo(() => {
		return (
			REQUIRED_WORKFLOW[currentStep] || {
				section: null,
				type: null,
				component: null
			}
		)
	}, [REQUIRED_WORKFLOW, currentStep])

	const canProceed = useMemo(() => {
		return canProceedFunc(wizardContextProvider.wizard_state, currentWorflowStepConfig)
	}, [canProceedFunc, wizardContextProvider, currentWorflowStepConfig])

	const hideActionsOtherThanClose = useMemo(() => {
		const { type } = currentWorflowStepConfig
		if (type === WIZARD_SCREEN_TYPES.Start || type === WIZARD_SCREEN_TYPES.Exit) {
			return true
		}
		return false
	}, [currentWorflowStepConfig])

	const hideNext = useMemo(() => {
		const { type } = currentWorflowStepConfig
		if (type === WIZARD_SCREEN_TYPES.Invoice || type === WIZARD_SCREEN_TYPES.Final) {
			return true
		}
		return false
	}, [currentWorflowStepConfig])

	const currentSection = useMemo(() => {
		const { section, type } = currentWorflowStepConfig
		if (type && type === WIZARD_SCREEN_TYPES.Intro) {
			return null
		}

		return section
	}, [currentWorflowStepConfig])

	const currentAttribute = useMemo(() => {
		const { attr } = currentWorflowStepConfig || ''
		return attr
	}, [currentWorflowStepConfig])

	const requiresApiUpdate = useMemo(() => {
		const { requires_api_update } = currentWorflowStepConfig || false
		return requires_api_update
	}, [currentWorflowStepConfig])

	const handleUpdate = useCallback(async () => {
		const { wizard_state } = wizardContextProvider
		const {
			_id: visitId,
			copy_food_addons_to_all_pets,
			copy_other_addons_to_all_pets,
			copy_kennel_options_to_all_pets,
			copy_kennel_options_from_pet_id,
			extended_pickup,
			early_dropoff,
			pets
		} = wizard_state
		const value = wizard_state[currentAttribute]
		if (value !== undefined && requiresApiUpdate) {
			switch (currentAttribute) {
				case BOARDING_FIELDS.early_dropoff:
					await update({
						visitId,
						updates: { [currentAttribute]: value, extended_pickup }
					})
					break
				case BOARDING_FIELDS.extended_pickup:
					await update({
						visitId,
						updates: { [currentAttribute]: value, early_dropoff }
					})
					break
				case BOARDING_FIELDS.boarding_options:
					let baseBoardingOptions = [...value]

					if (copy_kennel_options_to_all_pets === true && copy_kennel_options_from_pet_id > '') {
						const petToCopyFrom = value.find(
							({ pet_id }) => pet_id === copy_kennel_options_from_pet_id
						)
						if (petToCopyFrom) {
							const { kennel_type } = petToCopyFrom
							baseBoardingOptions = value.map((option) => {
								if (option.hasOwnProperty('kennel_type')) {
									return { ...option, kennel_type }
								} else {
									return option
								}
							})
						}
					}

					await update({ visitId, updates: { [currentAttribute]: baseBoardingOptions } })
					break
				case BOARDING_FIELDS.pet_food_schedules:
					let schedules = { ...value }

					if (copy_food_addons_to_all_pets === true && pets.length > 0) {
						const sourceSchedule = schedules[pets[0]._id] || pets[0].food_schedule || []
						pets.forEach(({ _id }) => {
							schedules[_id] = [...sourceSchedule]
						})
					}

					const updatedPets = [...pets]
					let anyScheduleChanged = false

					for (let i = 0; i < updatedPets.length; i++) {
						const pet = updatedPets[i]
						const newSchedule = schedules[pet._id] || []
						if (!newSchedule.length) {
							continue
						}
						const sortedNew = [...newSchedule].sort()
						const sortedOriginal = [...(pet.food_schedule || [])].sort()

						if (!isEqual(sortedNew, sortedOriginal)) {
							anyScheduleChanged = true
							await updatePetAsync(dispatch, pet._id, { food_schedule: newSchedule })
							updatedPets[i] = { ...pet, food_schedule: newSchedule }
						}
					}

					if (anyScheduleChanged) {
						setWizardAttributeAndValues({
							...wizard_state,
							[BOARDING_FIELDS.pets]: updatedPets,
							[BOARDING_FIELDS.pet_food_schedules]: schedules
						})
					}
					break
				case BOARDING_FIELDS.add_on_services:
					let baseValues = [...value]

					if (copy_other_addons_to_all_pets === true) {
						const existingAddonPetIdSet = new Set()
						const missingAddonPetIds = new Set()

						const otherAddons = value.filter(({ category }) => category !== 'meal')

						otherAddons.forEach(({ pet_id }) => {
							existingAddonPetIdSet.add(pet_id)
						})

						pets.forEach(({ _id }) => {
							if (!existingAddonPetIdSet.has(_id)) {
								missingAddonPetIds.add(_id)
							}
						})

						if (missingAddonPetIds.size > 0) {
							for (const pet_id of missingAddonPetIds.values()) {
								const newOtherAddons = otherAddons.map((addon) => {
									return { ...addon, pet_id }
								})
								baseValues = [...baseValues, ...newOtherAddons]
							}
						}
					}

					const petIds = pets.map(({ _id }) => _id)
					await updateAddOns({ visitId, addons: baseValues, petIds })
					break
				case BOARDING_FIELDS.first_day_daycare:
					await update({
						visitId,
						updates: {
							[currentAttribute]: value,
							[BOARDING_FIELDS.early_dropoff]: wizard_state[BOARDING_FIELDS.early_dropoff]
						}
					})
					break
				case BOARDING_FIELDS.last_day_daycare:
					await update({
						visitId,
						updates: {
							[currentAttribute]: value,
							[BOARDING_FIELDS.extended_pickup]: wizard_state[BOARDING_FIELDS.extended_pickup]
						}
					})
					break
				default:
					await update({ visitId, updates: { [currentAttribute]: value } })
					break
			}
		}
	}, [wizardContextProvider, currentAttribute, requiresApiUpdate, update, updateAddOns, dispatch, setWizardAttributeAndValues])

	const incrementStep = useCallback(
		async (params = { by: 1, withSave: true }) => {
			const curStep = parseInt(currentStep, 10)
			if (curStep !== requiredSteps) {
				if (params.withSave) {
					await handleUpdate()
				}
				const goToStep = curStep + params.by
				setCurrentStep(goToStep)
				setStepInHistory(goToStep)
			}
		},
		[currentStep, setCurrentStep, setStepInHistory, requiredSteps, handleUpdate]
	)

	const decrementStep = useCallback(
		async (params = { by: 1, withSave: true }) => {
			const curStep = parseInt(currentStep, 10)
			if (curStep !== 0) {
				if (params.withSave) {
					await handleUpdate()
				}
				const goToStep = curStep - params.by
				setCurrentStep(goToStep)
				setStepInHistory(goToStep)
			}
		},
		[currentStep, setCurrentStep, setStepInHistory, handleUpdate]
	)

	const currentComponent = useMemo(() => {
		const {
			component,
			can_proceed_condition,
			attr,
			type,
			individual_props_for_each_view_component,
			copy_to_all_attr,
			position,
			goToStep
		} = currentWorflowStepConfig
		if (!component) {
			return <div>I dont seem to have that step!</div>
		}

		if (can_proceed_condition) {
			setCanProceedFunc(() => can_proceed_condition)
		} else {
			setCanProceedFunc(() => () => true)
		}

		const props = {}
		if (type === WIZARD_SCREEN_TYPES.Start) {
			props['onAction'] = handleStart
		}
		if (type === WIZARD_SCREEN_TYPES.Exit) {
			props['onAction'] = handleExit
		}
		if (attr) {
			props['attr'] = attr
		}
		if (copy_to_all_attr) {
			props['copy_to_all_attr'] = copy_to_all_attr
		}
		if (type === WIZARD_SCREEN_TYPES.Invoice || type === WIZARD_SCREEN_TYPES.Final) {
			// The invoice view is the last screen and the onComplete
			// is called after the booking is confirmed.
			props['onComplete'] = incrementStep
		}
		if (individual_props_for_each_view_component) {
			individual_props_for_each_view_component.forEach((key) => {
				props[key] = currentWorflowStepConfig[key]
			})
		}
		if (position && position === -1) {
			props['first_view_of_multiple'] = true
		}
		props['petNames'] = formatArrayOfPetNames(wizardContextProvider.wizard_state.petNames) || 'doggos'
		props['userName'] = userFirstName
		props['goToStep'] = goToStep

		return component(props)
	}, [currentWorflowStepConfig, userFirstName, wizardContextProvider, handleStart, handleExit])

	return (
		<BoardingWizardContext.Provider value={{ wizardContextProvider }}>
			<div>
				<WizardDialog
					open={open}
					hideBack={hideActionsOtherThanClose}
					hideFooter={hideActionsOtherThanClose || hideNext}
					disableNext={!canProceed || loading}
					disableBack={loading}
					onClose={handleCloseRequest}
					onNext={incrementStep}
					onBack={decrementStep}
					loading={loading}>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							flex: 1,
							gap: '1rem',
							opacity: loading ? 0.5 : 1
						}}>
						<DifferentFacilityBookingWarning
							facility={facility}
							defaultFacilityId={household?.default_facility_id}
						/>
						{currentSection ? (
							<SectionNavigator sections={SECTIONS} activeSectionId={currentSection} />
						) : null}
						{currentComponent}
					</div>
				</WizardDialog>
				<ExitWizardDialog
					title="Exit Wizard?"
					description="Are you sure you want to exit the wizard? You may never be able to return!"
					open={showExitWizardDialog}
					onExit={handleOnClose}
					onCancel={handleCloseRequestCancel}
				/>
			</div>
		</BoardingWizardContext.Provider>
	)
}

export default BoardingWizard
